import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Loader2, RotateCcw, X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

const MAX_SECONDS = 180;
const pickMime = () => ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || '';
export const recordingSupported = typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && !!window.MediaRecorder;

// Transcribe one clip through the diary-transcribe Edge Function (audio held in memory only; nothing stored).
export async function transcribeClip(organisationId, blob, seconds) {
  const fd = new FormData();
  fd.set('organisation_id', organisationId); fd.set('duration_s', String(Math.round(seconds)));
  fd.set('file', blob, `clip.${(blob.type.match(/mp4|webm|ogg|wav/i) || ['webm'])[0]}`);
  const { data, error } = await supabase.functions.invoke('diary-transcribe', { body: fd });
  if (error) { let detail = ''; try { detail = (await error.context?.json())?.error || ''; } catch { /* no body */ } throw new Error(detail || (error.message === 'Failed to send a request to the Edge Function' ? 'Could not reach the transcription service — check your signal and retry' : error.message)); }
  if (data?.error) throw new Error(data.error);
  return data;
}

// Record → Transcribe → Insert. States: idle | recording | uploading | failed (clip kept for retry).
export const RecordButton = ({ organisationId, onText, disabled, testId }) => {
  const [state, setState] = useState('idle');
  const [secs, setSecs] = useState(0);
  const [err, setErr] = useState('');
  const rec = useRef(null); const chunks = useRef([]); const clip = useRef(null); const timer = useRef(null); const stream = useRef(null);
  const cleanupStream = () => { stream.current?.getTracks().forEach((t) => t.stop()); stream.current = null; clearInterval(timer.current); };
  useEffect(() => () => { rec.current?.state === 'recording' && rec.current.stop(); cleanupStream(); }, []);
  if (!recordingSupported) return <span className="text-[11px] text-muted-foreground max-w-[280px] text-right" data-testid={`${testId}-unsupported`}>Voice notes aren't available in this browser. Use Chrome (Android/desktop) or Safari 14.3+ (iPhone), opened directly — not inside another app — and type the note instead.</span>;

  const upload = async (blob, seconds) => {
    setState('uploading'); setErr('');
    try {
      const res = await transcribeClip(organisationId, blob, seconds);
      if (!res.text) { toast.message('Nothing was heard in that recording'); } else onText(res.text);
      clip.current = null; setState('idle'); setSecs(0);
    } catch (e) { setErr(e.message); setState('failed'); }
  };
  const stop = () => { if (rec.current?.state === 'recording') rec.current.stop(); };
  const start = async () => {
    setErr('');
    let s;
    try { s = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch (e) {
      const denied = e?.name === 'NotAllowedError' || e?.name === 'SecurityError';
      setErr(denied ? 'Microphone blocked. Allow the microphone for this site in your browser/phone settings, then try again.' : e?.name === 'NotFoundError' ? 'No microphone found on this device.' : `Could not start the microphone (${e?.name || 'error'})`);
      setState('failed'); clip.current = null; return;
    }
    stream.current = s; chunks.current = [];
    const mime = pickMime();
    const r = new MediaRecorder(s, mime ? { mimeType: mime } : undefined);
    rec.current = r;
    const started = Date.now();
    r.ondataavailable = (e) => { if (e.data?.size) chunks.current.push(e.data); };
    r.onstop = () => {
      cleanupStream();
      const seconds = Math.min(MAX_SECONDS, (Date.now() - started) / 1000);
      const blob = new Blob(chunks.current, { type: r.mimeType || mime || 'audio/webm' });
      if (seconds < 0.8 || blob.size < 1000) { setState('idle'); setSecs(0); toast.message('Recording too short — hold the button and speak'); return; }
      clip.current = { blob, seconds };
      upload(blob, seconds);
    };
    r.start(1000); setState('recording'); setSecs(0);
    timer.current = setInterval(() => { const el = Math.floor((Date.now() - started) / 1000); setSecs(el); if (el >= MAX_SECONDS) { toast.message('3-minute limit reached — transcribing'); stop(); } }, 500);
  };
  const retry = () => clip.current ? upload(clip.current.blob, clip.current.seconds) : start();
  const discard = () => { clip.current = null; setErr(''); setState('idle'); setSecs(0); };
  const mmss = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
  const base = 'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors disabled:opacity-40';
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap justify-end" data-testid={`${testId}-wrap`}>
      {state === 'idle' && <button type="button" data-testid={testId} disabled={disabled} onClick={start} aria-label="Record voice note" className={`${base} border-border bg-white text-charcoal hover:border-bronze/60`}><Mic size={12} /> Record</button>}
      {state === 'recording' && <button type="button" data-testid={testId} onClick={stop} aria-label="Stop and transcribe" className={`${base} border-red-300 bg-red-50 text-red-700`}><Square size={12} className="animate-pulse" /> Stop · <span data-testid={`${testId}-timer`} className="tabular-nums">{mmss}</span> / 3:00</button>}
      {state === 'uploading' && <span data-testid={`${testId}-busy`} className={`${base} border-border bg-[#F7F5F1] text-muted-foreground`}><Loader2 size={12} className="animate-spin" /> Transcribing…</span>}
      {state === 'failed' && <>
        <span className="text-[11px] text-red-700 max-w-[260px] text-right" data-testid={`${testId}-error`}>{err}</span>
        <button type="button" data-testid={`${testId}-retry`} onClick={retry} className={`${base} border-border bg-white text-charcoal hover:border-bronze/60`}><RotateCcw size={12} /> {clip.current ? 'Retry upload' : 'Try again'}</button>
        <button type="button" data-testid={`${testId}-discard`} onClick={discard} aria-label="Discard" className={`${base} border-border bg-white text-muted-foreground`}><X size={12} /></button>
      </>}
    </span>
  );
};
